<script setup lang="ts">
    import slugify from '~/utils/slugify';

    const {
        title,
        panelKey = null,
        initialOpen = false
    } = defineProps<{
        title: string;
        panelKey?: string;
        initialOpen?: boolean;
    }>();

    const open = ref(initialOpen ?? false);

    const ids = computed(() => {
        const key = panelKey ?? slugify(title);

        return {
            buttonId: `panel-button-${key}`,
            panelId: `panel-${key}`
        };
    });
</script>

<template>
    <div class="panel">
        <button
            :id="ids.buttonId"
            type="button"
            class="trigger"
            :aria-expanded="open"
            :aria-controls="ids.panelId"
            @click="open = !open"
        >
            <span
                class="title"
                :title="title"
                >{{ title }}</span
            >
            <Icon
                name="lucide:chevron-right"
                class="chevron"
                :class="{ open }"
                size="1.375rem"
                aria-hidden="true"
            />
        </button>

        <Transition name="expand">
            <div
                v-show="open"
                :id="ids.panelId"
                class="content"
                role="region"
                :aria-labelledby="ids.buttonId"
            >
                <slot />
            </div>
        </Transition>
    </div>
</template>

<style lang="scss" scoped>
    @import '@/assets/scss/global/transitions';

    .trigger {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: var(--spacing--05);
        width: 100%;
        padding: var(--spacing--05) var(--spacing);
        border: none;
        appearance: none;
        background-color: transparent;
        cursor: pointer;
        text-align: left;
        font: inherit;
        color: inherit;

        &:focus-visible {
            outline: none;
            box-shadow: inset 0 0 0 4px var(--color-focus);
        }

        .title {
            font-weight: 700;
            text-rendering: geometricPrecision;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            max-width: 100%;
        }
    }

    .chevron {
        flex-shrink: 0;
        transition: transform 0.2s ease-in-out;

        &.open {
            transform: rotate(90deg);
        }

        @media (min-width: $br-l) {
            transform: rotate(90deg);

            &.open {
                transform: rotate(-90deg);
            }
        }
    }

    .content {
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }
</style>
